import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import SignatureCanvas from 'react-signature-canvas';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CameraIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ClipboardDocumentCheckIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';

const MobileInspectionForm = () => {
  const { inspectionType, inspectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Once an inspection is saved (has an id), only managers/admins may edit it.
  const INSPECTION_EDIT_ROLES = ['super_admin', 'admin', 'shop_foreman', 'fabrication_manager'];
  const locked = !!inspectionId && !INSPECTION_EDIT_ROLES.includes(user?.role);
  
  const [inspection, setInspection] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [forklifts, setForklifts] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [signature, setSignature] = useState(null);
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [towing, setTowing] = useState('no'); // truck inspections: towing a trailer today?

  // Editing an existing truck inspection: restore the towing answer from the saved data
  useEffect(() => {
    if (loading || !inspectionId || inspectionType !== 'truck_inspection' || !inspection) return;
    const t = (inspection.inspection_items || []).find(i => (i.description || '').toLowerCase().startsWith('trailer / towed unit'));
    if (t && (t.notes || '').trim() && !t.notes.startsWith('N/A')) setTowing('yes');
  }, [loading]); // eslint-disable-line
  const sigCanvas = useRef(null);
  const [qrReturn, setQrReturn] = useState(null);
  const [formData, setFormData] = useState({
    job_site: '',
    work_order_number: '',
    equipment_id: '',
    equipment_make_model: '',
    weather_conditions: '',
    temperature: '',
    notes: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const inspectionTypeNames = {
    'daily_psi': 'Daily PSI',
    'truck_inspection': 'Truck Inspection',
    'forklift_inspection': 'Forklift Inspection',
    'harness_lanyard_inspection': 'Harness & Lanyard',
    'trailer_inspection': 'Trailer Inspection',
    'confined_space_permit': 'Confined Space Entry Permit',
    'lockout_tagout': 'Lockout / Tagout'
  };

  useEffect(() => {
    if (inspectionId) {
      loadExistingInspection();
    } else {
      loadTemplate();
      // Pre-select equipment + remember origin when arriving from a forklift QR scan
      const _eq = new URLSearchParams(window.location.search).get('equipment_id');
      if (_eq) {
        setFormData(prev => ({ ...prev, equipment_id: _eq }));
        setQrReturn(_eq);
      }
    }
    
    // Load equipment for autocomplete
    if (inspectionType === 'truck_inspection') {
      loadTrucks();
    } else if (inspectionType === 'forklift_inspection') {
      console.log('Loading forklifts for inspection:', inspectionType);
      loadForklifts();
    } else if (inspectionType === 'trailer_inspection') {
      loadTrailers();
    }
  }, [inspectionType, inspectionId]);
  
  const loadTrucks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/owned-equipment`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const equipment = await response.json();
        // Filter for trucks only
        const truckList = equipment.filter(e => 
          e.equipment_type?.toLowerCase().includes('truck') || 
          e.equipment_category?.toLowerCase().includes('truck')
        );
        setTrucks(truckList);
      }
    } catch (error) {
      console.error('Failed to load trucks:', error);
    }
  };
  
  const loadForklifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/owned-equipment`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const equipment = await response.json();
        // Filter for forklifts - check type, category, name, OR specific IDs
        const forkliftList = equipment.filter(e => {
          const type = (e.equipment_type || '').toLowerCase();
          const category = (e.equipment_category || '').toLowerCase();
          const name = (e.equipment_name || '').toLowerCase();
          const id = (e.equipment_id || e.id || '').toUpperCase();
          
          return type.includes('forklift') || 
                 category.includes('forklift') ||
                 name.includes('forklift') ||
                 name.includes('komatsu') ||
                 name.includes('yale') ||
                 name.includes('taylor') ||
                 name.includes('hyster') ||
                 ['F1', 'F2', 'F3', 'F4'].includes(id);
        });
        console.log(`Loaded ${forkliftList.length} forklifts for dropdown (total equipment: ${equipment.length})`);
        setForklifts(forkliftList);
      }
    } catch (error) {
      console.error('Failed to load forklifts:', error);
    }
  };

  const loadTrailers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/owned-equipment`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const equipment = await response.json();
        const trailerList = equipment.filter(e => {
          const type = (e.equipment_type || '').toLowerCase();
          const category = (e.equipment_category || '').toLowerCase();
          const name = (e.equipment_name || '').toLowerCase();
          return type.includes('trailer') || category.includes('trailer') || name.includes('trailer') || name.includes('float');
        });
        setTrailers(trailerList);
      }
    } catch (error) {
      console.error('Failed to load trailers:', error);
    }
  };

  
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignature(null);
  };
  
  const saveSignature = () => {
    if (sigCanvas.current) {
      const signatureData = sigCanvas.current.toDataURL();
      setSignature(signatureData);
    }
  };

  const loadExistingInspection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/inspections/${inspectionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInspection(data);
        setFormData({
          job_site: data.job_site || '',
          work_order_number: data.work_order_number || '',
          equipment_id: data.equipment_id || '',
          equipment_make_model: data.equipment_make_model || '',
          weather_conditions: data.weather_conditions || '',
          temperature: data.temperature || '',
          notes: data.corrective_actions || ''
        });
      }
    } catch (error) {
      console.error('Failed to load inspection:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/inspections/templates/${inspectionType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplate(data.template_items);
        
        // Initialize new inspection
        setInspection({
          inspection_type: inspectionType,
          inspection_items: data.template_items.map((item, index) => ({
            id: `item_${index}`,
            description: item.description || item.item || 'Inspection item',
            category: item.category || 'general',
            type: item.type || 'pass_fail',  // Preserve field type
            group: item.group,  // Preserve radio group
            status: item.status !== undefined ? item.status : null,
            notes: item.notes || '',
            photos: [],
            photo_urls: []
          })),
          overall_status: null,
          deficiencies_noted: false
        });
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateInspectionItem = (itemId, field, value) => {
    if (locked) return;
    setInspection(prev => ({
      ...prev,
      inspection_items: prev.inspection_items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  // Compress and resize image before saving
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to 70% quality JPEG
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle photo capture from camera
  const handlePhotoCapture = async (e, itemId) => {
    const file = e.target.files[0];
    if (file) {
      const compressedPhoto = await compressImage(file);
      setInspection(prev => ({
        ...prev,
        inspection_items: prev.inspection_items.map(item => {
          if (item.id === itemId) {
            const photos = item.photos || [];
            return { ...item, photos: [...photos, compressedPhoto] };
          }
          return item;
        })
      }));
    }
    e.target.value = '';
  };

  // Handle photo upload (multiple files)
  const handlePhotoUpload = async (e, itemId) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const compressedPhotos = await Promise.all(files.map(file => compressImage(file)));
      setInspection(prev => ({
        ...prev,
        inspection_items: prev.inspection_items.map(item => {
          if (item.id === itemId) {
            const photos = item.photos || [];
            return { ...item, photos: [...photos, ...compressedPhotos] };
          }
          return item;
        })
      }));
    }
    e.target.value = '';
  };

  // Remove photo
  const removePhoto = (itemId, photoIndex) => {
    setInspection(prev => ({
      ...prev,
      inspection_items: prev.inspection_items.map(item => {
        if (item.id === itemId && item.photos) {
          return {
            ...item,
            photos: item.photos.filter((_, i) => i !== photoIndex)
          };
        }
        return item;
      })
    }));
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Truck inspections: selecting the truck auto-fills unit #, make/model/year and plate
  const handleTruckSelect = (name) => {
    updateFormData('equipment_id', name);
    const truck = trucks.find(t => t.equipment_name === name);
    if (!truck) return;
    const autoFill = [
      ['unit / truck number', truck.id || name],
      ['make / model / year', [truck.make, truck.model, truck.year].filter(Boolean).join(' ')],
      ['licence plate', truck.license_plate || ''],
    ];
    setInspection(prev => !prev ? prev : ({
      ...prev,
      inspection_items: prev.inspection_items.map(item => {
        const desc = (item.description || '').toLowerCase();
        const match = autoFill.find(([key, val]) => desc.startsWith(key) && val);
        return match ? { ...item, notes: String(match[1]), status: 'completed' } : item;
      })
    }));
  };

  const calculateOverallStatus = (itemsOverride) => {
    const items = itemsOverride || (inspection && inspection.inspection_items);
    if (!items) return null;
    
    const hasUnselected = items.some(item => item.status === null || item.status === undefined);
    const hasFailures = items.some(item => item.status === 'fail');
    const hasAttention = items.some(item => item.status === 'needs_attention');
    
    // If any items are unselected, inspection is incomplete
    if (hasUnselected) return null;
    
    if (hasFailures) return 'fail';
    if (hasAttention) return 'needs_attention';
    return 'pass';
  };

  const formatCategoryName = (category) => {
    // Convert underscores to spaces and capitalize each word
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const saveInspection = async () => {
    if (locked) return;
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');

      // Defects/remarks is only required when something was actually flagged
      const anyDefects = inspection.inspection_items.some(i => i.status === 'fail' || i.status === 'needs_attention');

      // Check if inspection is complete
      const incompleteItems = inspection.inspection_items.filter(item => {
        const desc = (item.description || '').toLowerCase();
        // For text/textarea/date/time fields, check if notes are filled
        if (['text', 'textarea', 'date', 'time'].includes(item.type)) {
          if (towing === 'no' && desc.startsWith('trailer / towed unit')) return false;
          if (desc.startsWith('defects / remarks') && !anyDefects) return false;
          return !item.notes || item.notes.trim() === '';
        }
        // For checkbox/radio fields, they're optional (can be unchecked)
        if (['checkbox', 'radio'].includes(item.type)) {
          return false; // Don't require checkboxes to be checked
        }
        // For pass/fail fields, require a status
        return item.status === null || item.status === undefined;
      });
      
      if (incompleteItems.length > 0) {
        // Highlight missing fields in red
        setInvalidFields(new Set(incompleteItems.map(item => item.id)));
        
        alert(`❌ INCOMPLETE FORM\n\n${incompleteItems.length} field(s) still need to be completed. Missing fields are highlighted in RED.`);
        setSaving(false);
        return;
      }
      
      // Clear invalid fields if validation passes
      setInvalidFields(new Set());

      // NEW: forklift/truck/trailer inspections must have an equipment selected — a blank
      // equipment_id is what caused inspections to silently drop off Foreman Review.
      if ((inspectionType === 'forklift_inspection' || inspectionType === 'truck_inspection' || inspectionType === 'trailer_inspection') &&
          (!formData.equipment_id || !formData.equipment_id.trim())) {
        const label = inspectionType === 'forklift_inspection' ? 'forklift' : (inspectionType === 'trailer_inspection' ? 'trailer' : 'truck');
        alert(`❌ ${label.toUpperCase()} REQUIRED\n\nPlease select the ${label} you inspected before submitting.`);
        setSaving(false);
        return;
      }

      // NEW: Check if signature is required but missing
      const requiresSignature = ['truck_inspection', 'forklift_inspection', 'aerial_lift_inspection', 
                                 'overhead_crane_inspection', 'harness_lanyard_inspection',
                                 'trailer_inspection', 'confined_space_permit', 'lockout_tagout'].includes(inspectionType);
      if (requiresSignature && !signature) {
        alert('❌ SIGNATURE REQUIRED\n\nPlease sign the inspection before submitting. Scroll down to the Inspector Signature section.');
        setSaving(false);
        return;
      }
      
      // NEW: Check if defects have required photos
      const defectsWithoutPhotos = inspection.inspection_items.filter(item => 
        item.status === 'fail' && (!item.photos || item.photos.length === 0)
      );
      
      if (defectsWithoutPhotos.length > 0) {
        alert(`❌ PHOTOS REQUIRED: All defects must have at least one photo. ${defectsWithoutPhotos.length} defect(s) still need photos.`);
        setSaving(false);
        return;
      }
      
      // Check if defects have notes
      const defectsWithoutNotes = inspection.inspection_items.filter(item => 
        (item.status === 'fail' || item.status === 'needs_attention') && (!item.notes || item.notes.trim() === '')
      );
      
      if (defectsWithoutNotes.length > 0) {
        alert(`❌ NOTES REQUIRED: All defects and issues must have detailed notes. ${defectsWithoutNotes.length} item(s) still need notes.`);
        setSaving(false);
        return;
      }
      
      // Auto-fill the fields the driver was allowed to skip
      const preparedItems = inspection.inspection_items.map(item => {
        const desc = (item.description || '').toLowerCase();
        if (towing === 'no' && desc.startsWith('trailer / towed unit') && !(item.notes || '').trim()) {
          return { ...item, notes: 'N/A — not towing', status: 'completed' };
        }
        if (desc.startsWith('defects / remarks') && !(item.notes || '').trim() && !anyDefects) {
          return { ...item, notes: 'No defects noted', status: 'completed' };
        }
        return item;
      });
      const overallStatus = calculateOverallStatus(preparedItems);

      const inspectionData = {
        ...inspection,
        ...formData,
        inspection_items: preparedItems,
        overall_status: overallStatus,
        deficiencies_noted: overallStatus !== 'pass',
        corrective_actions: formData.notes,
        inspector_signature: signature  // Add signature data
      };

      // Debug logging
      console.log('=== SAVING INSPECTION ===');
      console.log('Inspection Type:', inspectionData.inspection_type);
      console.log('Inspector Signature:', signature ? 'Present' : 'Missing');
      console.log('Number of inspection items:', inspectionData.inspection_items?.length);
      
      // Count photos
      const photoCount = inspectionData.inspection_items?.reduce((sum, item) => {
        return sum + (item.photos?.length || 0);
      }, 0);
      console.log('Total photos:', photoCount);
      console.log('Sample item with photos:', inspectionData.inspection_items?.find(item => item.photos?.length > 0));

      let response;
      if (inspectionId) {
        // Update existing inspection
        response = await fetch(`${backendUrl}/api/inspections/${inspectionId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(inspectionData)
        });
      } else {
        // Create new inspection
        response = await fetch(`${backendUrl}/api/inspections/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(inspectionData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        const savedInspectionId = result.id || result.inspection_id;

        // Oil change due? (backend tracks the odometer every truck inspection)
        if (result.oil_change_due) {
          const d = result.oil_change_due;
          const alreadyDone = window.confirm(
            `🛢️ OIL CHANGE DUE — ${d.truck}\n\n` +
            `${Number(d.km_since_oil_change).toLocaleString()} km since the last oil change (due every 10,000 km).\n` +
            `Current odometer: ${Number(d.current_km).toLocaleString()} km\n\n` +
            `${d.shop_message}\n\n` +
            `If the oil HAS ALREADY been changed recently, press OK to reset the counter.\n` +
            `Otherwise press Cancel and get it booked in.`
          );
          if (alreadyDone) {
            try {
              await fetch(`${backendUrl}/api/equipment/${d.equipment_id}/oil-change`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ km: d.current_km })
              });
              alert('✅ Oil change counter reset.');
            } catch (e) { console.error('Oil change reset failed:', e); }
          }
        }

        // Confined space permits stay OPEN — no PDF yet; it's generated on close-out.
        if (!inspectionId && inspectionType === 'confined_space_permit' && savedInspectionId) {
          alert('✅ PERMIT OPENED\n\nThis confined space permit is now OPEN.\nAdd atmospheric tests throughout the shift from the permit page, and close it out (with signatures) when access is complete.');
          navigate(`/mobile/permit/${savedInspectionId}`);
          return;
        }

        // Auto-generate PDF and upload to Google Drive
        if (savedInspectionId) {
          try {
            const pdfResponse = await fetch(`${backendUrl}/api/inspections/${savedInspectionId}/generate-pdf`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (pdfResponse.ok) {
              const pdfResult = await pdfResponse.json();
              if (pdfResult.google_drive_link) {
                alert(`Inspection saved and uploaded to Google Drive successfully!\n\nView PDF: ${pdfResult.google_drive_link}`);
              } else {
                alert('Inspection saved and PDF generated successfully!');
              }
            } else {
              const pdfError = await pdfResponse.text();
              console.error('PDF generation error:', pdfError);
              alert('Inspection saved successfully! (PDF generation may have failed)');
            }
          } catch (pdfError) {
            console.error('PDF generation failed:', pdfError);
            alert('Inspection saved successfully! (PDF generation may have failed)');
          }
        } else {
          alert('Inspection saved successfully!');
        }
        
        if (qrReturn) {
          // Came from a forklift QR scan — return to that unit's fresh status page
          window.location.href = `${backendUrl}/api/public/inspection-page/${encodeURIComponent(qrReturn)}`;
        } else {
          navigate('/mobile/inspections');
        }
      } else {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || 'Unknown error' };
        }
        console.error('Save error response:', errorData);
        console.error('Response status:', response.status);
        console.error('Full error:', errorText);
        throw new Error(errorData.detail || errorText || 'Failed to save inspection');
      }
    } catch (error) {
      console.error('Save failed:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      alert(`Failed to save inspection: ${error.message}\n\nPlease check browser console for full details.`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'border-green-500 bg-green-50';
      case 'fail': return 'border-red-500 bg-red-50';
      case 'needs_attention': return 'border-yellow-500 bg-yellow-50';
      case 'na': return 'border-gray-400 bg-gray-100';
      case null:
      case undefined: return 'border-gray-400 bg-gray-50 border-2 border-dashed';
      default: return 'border-gray-400 bg-gray-50 border-2 border-dashed';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return CheckCircleIcon;
      case 'fail': return XCircleIcon;
      case 'needs_attention': return ExclamationTriangleIcon;
      case 'na': return NoSymbolIcon;
      case null:
      case undefined: return DocumentTextIcon;
      default: return DocumentTextIcon;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load inspection</p>
          <button 
            onClick={() => navigate('/mobile/inspections')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-lg">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/mobile/inspections')}
            className="mr-4"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">
              {inspectionTypeNames[inspectionType] || 'Inspection'}
            </h1>
            <p className="text-blue-800 text-sm">
              {inspectionId ? 'Edit inspection' : 'New inspection'}
            </p>
          </div>
        </div>
      </div>

      {locked && (
        <div className="bg-amber-50 border-b border-amber-300 p-4" data-testid="inspection-locked-banner">
          <div className="flex items-start gap-2">
            <NoSymbolIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium">
              This inspection is locked. Only a manager (Shop Foreman or Fabrication Manager) or an admin can make changes. You can view it but not edit.
            </p>
          </div>
        </div>
      )}

      <fieldset disabled={locked} className="contents">

      {inspection && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-900">Inspection Progress</h3>
              <p className="text-sm text-blue-700">
                {inspection.inspection_items.filter(item => item.status !== null && item.status !== undefined).length} of {inspection.inspection_items.length} items completed
              </p>
            </div>
            <div className="text-right">
              {calculateOverallStatus() === null ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  Incomplete
                </span>
              ) : (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  calculateOverallStatus() === 'pass' ? 'bg-green-100 text-green-800' :
                  calculateOverallStatus() === 'fail' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {calculateOverallStatus() === 'pass' ? '✓ Pass' :
                   calculateOverallStatus() === 'fail' ? '🚫 Do Not Use' :
                   '⚠ Usable — Defects Flagged'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Basic Information */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            {/* Work Order field for project-based inspections */}
            {(inspectionType === 'daily_psi' || 
              inspectionType === 'harness_lanyard_inspection' || 
              inspectionType === 'aerial_lift_inspection' || 
              inspectionType === 'overhead_crane_inspection' || 
              inspectionType === 'accident_incident_report' || 
              inspectionType === 'confined_space_permit' || 
              inspectionType === 'lockout_tagout' || 
              inspectionType === 'hot_work_permit') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Order Number
                </label>
                <input
                  type="text"
                  value={formData.work_order_number}
                  onChange={(e) => updateFormData('work_order_number', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Enter work order (e.g., 25-1002)"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Site
              </label>
              <input
                type="text"
                value={formData.job_site}
                onChange={(e) => updateFormData('job_site', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                placeholder="Enter job site location"
              />
            </div>

            {inspectionType !== 'daily_psi' && 
             inspectionType !== 'harness_lanyard_inspection' && 
             inspectionType !== 'accident_incident_report' && 
             inspectionType !== 'confined_space_permit' && 
             inspectionType !== 'lockout_tagout' && 
             inspectionType !== 'hot_work_permit' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {inspectionType === 'truck_inspection' ? 'Truck Number' : 
                     inspectionType === 'forklift_inspection' ? 'Forklift' : 
                     inspectionType === 'trailer_inspection' ? 'Trailer' : 
                     'Equipment ID'}
                  </label>

                  {/* Forklift: strict dropdown — prevents blank/garbage submissions
                      that get dropped from Foreman Review */}
                  {inspectionType === 'forklift_inspection' && forklifts.length > 0 && (
                    <>
                      <select
                        data-testid="forklift-select"
                        value={formData.equipment_id}
                        onChange={(e) => updateFormData('equipment_id', e.target.value)}
                        className={`w-full p-3 border rounded-lg bg-white ${!formData.equipment_id ? 'border-red-400' : 'border-gray-300'}`}
                      >
                        <option value="">— Select a forklift —</option>
                        {forklifts.map(forklift => (
                          <option key={forklift.id} value={forklift.equipment_name}>
                            {forklift.equipment_name}
                          </option>
                        ))}
                      </select>
                      {!formData.equipment_id && (
                        <p className="text-xs text-red-500 mt-1">Select a forklift before submitting.</p>
                      )}
                    </>
                  )}

                  {/* Truck: strict dropdown */}
                  {inspectionType === 'truck_inspection' && trucks.length > 0 && (
                    <>
                      <select
                        data-testid="truck-select"
                        value={formData.equipment_id}
                        onChange={(e) => handleTruckSelect(e.target.value)}
                        className={`w-full p-3 border rounded-lg bg-white ${!formData.equipment_id ? 'border-red-400' : 'border-gray-300'}`}
                      >
                        <option value="">— Select a truck —</option>
                        {trucks.map(truck => (
                          <option key={truck.id} value={truck.equipment_name}>
                            {truck.equipment_name}
                          </option>
                        ))}
                      </select>
                      {!formData.equipment_id && (
                        <p className="text-xs text-red-500 mt-1">Select a truck before submitting.</p>
                      )}
                      {formData.equipment_id && (
                        <p className="text-xs text-green-600 mt-1" data-testid="truck-autofill-note">✓ Unit #, make/model and plate filled in automatically</p>
                      )}
                    </>
                  )}

                  {/* Trailer: strict dropdown */}
                  {inspectionType === 'trailer_inspection' && trailers.length > 0 && (
                    <>
                      <select
                        data-testid="trailer-select"
                        value={formData.equipment_id}
                        onChange={(e) => updateFormData('equipment_id', e.target.value)}
                        className={`w-full p-3 border rounded-lg bg-white ${!formData.equipment_id ? 'border-red-400' : 'border-gray-300'}`}
                      >
                        <option value="">— Select a trailer —</option>
                        {trailers.map(trailer => (
                          <option key={trailer.id} value={trailer.equipment_name}>
                            {trailer.equipment_name}
                          </option>
                        ))}
                      </select>
                      {!formData.equipment_id && (
                        <p className="text-xs text-red-500 mt-1">Select a trailer before submitting.</p>
                      )}
                    </>
                  )}

                  {/* Fallback free-text: other equipment types, or when no units loaded */}
                  {((inspectionType === 'forklift_inspection' && forklifts.length === 0) ||
                    (inspectionType === 'truck_inspection' && trucks.length === 0) ||
                    (inspectionType === 'trailer_inspection' && trailers.length === 0) ||
                    (inspectionType !== 'forklift_inspection' && inspectionType !== 'truck_inspection' && inspectionType !== 'trailer_inspection')) && (
                    <input
                      type="text"
                      data-testid="inspection-equipment-input"
                      value={formData.equipment_id}
                      onChange={(e) => updateFormData('equipment_id', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder={
                        inspectionType === 'truck_inspection' ? 'No trucks in system — type truck number' : 
                        inspectionType === 'forklift_inspection' ? 'No forklifts in system — type forklift number' :
                        inspectionType === 'trailer_inspection' ? 'No trailers in system — type trailer number' :
                        'Enter equipment ID'
                      }
                    />
                  )}
                </div>

                {/* Truck: towing question — hides the trailer field when not towing */}
                {inspectionType === 'truck_inspection' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Towing a trailer today?</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-testid="towing-no"
                        onClick={() => setTowing('no')}
                        className={`flex-1 py-3 rounded-lg border-2 font-medium text-sm ${towing === 'no' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        data-testid="towing-yes"
                        onClick={() => setTowing('yes')}
                        className={`flex-1 py-3 rounded-lg border-2 font-medium text-sm ${towing === 'yes' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                      >
                        Yes — towing
                      </button>
                    </div>
                    {towing === 'no' && (
                      <p className="text-xs text-gray-500 mt-1">Trailer / Towed Unit field skipped — it'll be marked "N/A — not towing".</p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weather
                </label>
                <select
                  value={formData.weather_conditions}
                  onChange={(e) => updateFormData('weather_conditions', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="">Select...</option>
                  <option value="clear">Clear</option>
                  <option value="cloudy">Cloudy</option>
                  <option value="rain">Rain</option>
                  <option value="snow">Snow</option>
                  <option value="fog">Fog</option>
                  <option value="windy">Windy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature
                </label>
                <input
                  type="text"
                  value={formData.temperature}
                  onChange={(e) => updateFormData('temperature', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="°C"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Inspection Items - Enhanced Layout with Text Fields */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            {inspectionType === 'accident_incident_report' ? 'Incident Investigation Report' : 
             inspectionType === 'hot_work_permit' ? 'Hot Work Permit Details' :
             inspectionType === 'confined_space_permit' ? 'Confined Space Entry Permit' :
             'Equipment Inspection'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {inspectionType === 'accident_incident_report' 
              ? '⚠️ Complete all fields. Photos required for injuries.'
              : inspectionType === 'hot_work_permit'
              ? '⚠️ All safety requirements must be met before hot work begins.'
              : inspectionType === 'confined_space_permit'
              ? '⚠️ Submitting this form OPENS the permit. Add atmospheric tests throughout the shift, then close it out with signatures when access is complete.'
              : '⚠️ Photos REQUIRED for all defects. Complete each item before proceeding.'}
          </p>
          
          {/* Group items by category */}
          {(() => {
            const categories = {};
            inspection.inspection_items.forEach(item => {
              // Not towing today → the trailer/towed unit field disappears entirely
              if (towing === 'no' && (item.description || '').toLowerCase().startsWith('trailer / towed unit')) return;
              const cat = item.category || 'General';
              if (!categories[cat]) categories[cat] = [];
              categories[cat].push(item);
            });
            
            return Object.entries(categories).map(([category, items]) => (
              <div key={category} className="mb-6">
                {/* Category Header */}
                <h3 className="text-md font-semibold text-gray-800 bg-gray-100 px-3 py-2 rounded-t-lg border-b-2 border-blue-500">
                  {formatCategoryName(category)}
                </h3>
                
                <div className="space-y-3 bg-white p-3 rounded-b-lg border-2 border-gray-200 border-t-0">
                  {items.map((item) => {
                    const StatusIcon = getStatusIcon(item.status);
                    const requiresPhoto = item.status === 'fail';
                    const missingPhoto = requiresPhoto && (!item.photos || item.photos.length === 0);
                    const itemType = item.type || 'pass_fail';
                    const isInvalid = invalidFields.has(item.id);
                    
                    return (
                      <div key={item.id} className={`pb-3 border-b border-gray-200 last:border-b-0 ${isInvalid ? 'bg-red-50 border-2 border-red-500 rounded p-2' : ''}`}>
                        <label className={`block text-sm font-medium mb-2 ${isInvalid ? 'text-red-700' : 'text-gray-700'}`}>
                          {item.description}
                          {isInvalid && <span className="ml-2 text-red-600 font-bold">← REQUIRED</span>}
                        </label>

                        {/* Render based on field type */}
                        {itemType === 'text' && (
                          <input
                            type="text"
                            value={item.notes || ''}
                            onChange={(e) => {
                              updateInspectionItem(item.id, 'notes', e.target.value);
                              if (e.target.value.trim()) {
                                updateInspectionItem(item.id, 'status', 'completed');
                              }
                            }}
                            className="w-full p-2 border border-gray-300 rounded text-gray-900"
                            placeholder="Enter information..."
                          />
                        )}

                        {itemType === 'date' && (
                          <input
                            type="date"
                            value={item.notes || ''}
                            onChange={(e) => {
                              updateInspectionItem(item.id, 'notes', e.target.value);
                              updateInspectionItem(item.id, 'status', 'completed');
                            }}
                            className="w-full p-2 border border-gray-300 rounded text-gray-900"
                          />
                        )}

                        {itemType === 'time' && (
                          <input
                            type="time"
                            value={item.notes || ''}
                            onChange={(e) => {
                              updateInspectionItem(item.id, 'notes', e.target.value);
                              updateInspectionItem(item.id, 'status', 'completed');
                            }}
                            className="w-full p-2 border border-gray-300 rounded text-gray-900"
                          />
                        )}

                        {itemType === 'textarea' && (
                          <textarea
                            value={item.notes || ''}
                            onChange={(e) => {
                              updateInspectionItem(item.id, 'notes', e.target.value);
                              if (e.target.value.trim()) {
                                updateInspectionItem(item.id, 'status', 'completed');
                              }
                            }}
                            className="w-full p-2 border border-gray-300 rounded text-gray-900"
                            rows="3"
                            placeholder="Enter detailed description..."
                          />
                        )}

                        {itemType === 'checkbox' && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={item.status === 'yes' || item.status === 'checked'}
                              onChange={(e) => {
                                updateInspectionItem(item.id, 'status', e.target.checked ? 'yes' : 'no');
                              }}
                              className="w-5 h-5 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-600">
                              {item.status === 'yes' || item.status === 'checked' ? '✓ Yes' : 'No'}
                            </span>
                          </div>
                        )}

                        {itemType === 'radio' && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name={item.group || 'radio-group'}
                              checked={item.status === 'selected'}
                              onChange={(e) => {
                                const group = item.group;
                                if (group && e.target.checked) {
                                  setInspection(prev => ({
                                    ...prev,
                                    inspection_items: prev.inspection_items.map(it => 
                                      it.group === group && it.id !== item.id 
                                        ? {...it, status: 'unselected'} 
                                        : it.id === item.id 
                                          ? {...it, status: 'selected'}
                                          : it
                                    )
                                  }));
                                }
                              }}
                              className="w-4 h-4 text-blue-600"
                            />
                          </div>
                        )}

                        {(!itemType || itemType === 'pass_fail') && (
                    <>
                      {/* Status Selection - Pass / Minor Defect / Major Defect / N/A */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                        <button
                          onClick={() => updateInspectionItem(item.id, 'status', 'pass')}
                          data-testid={`item-${item.id}-pass`}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            item.status === 'pass'
                              ? 'bg-green-50 border-green-500 shadow-md'
                              : 'bg-white border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <CheckCircleIcon className={`h-6 w-6 mb-1 ${
                              item.status === 'pass' ? 'text-green-600' : 'text-slate-500'
                            }`} />
                            <span className={`text-sm font-medium ${
                              item.status === 'pass' ? 'text-green-700' : 'text-gray-600'
                            }`}>
                              Pass
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => updateInspectionItem(item.id, 'status', 'needs_attention')}
                          data-testid={`item-${item.id}-minor`}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            item.status === 'needs_attention'
                              ? 'bg-yellow-50 border-yellow-500 shadow-md'
                              : 'bg-white border-gray-200 hover:border-yellow-300'
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <ExclamationTriangleIcon className={`h-6 w-6 mb-1 ${
                              item.status === 'needs_attention' ? 'text-yellow-600' : 'text-slate-500'
                            }`} />
                            <span className={`text-sm font-medium text-center leading-tight ${
                              item.status === 'needs_attention' ? 'text-yellow-700' : 'text-gray-600'
                            }`}>
                              Minor Defect
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => updateInspectionItem(item.id, 'status', 'fail')}
                          data-testid={`item-${item.id}-major`}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            item.status === 'fail'
                              ? 'bg-red-50 border-red-500 shadow-md'
                              : 'bg-white border-gray-200 hover:border-red-300'
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <XCircleIcon className={`h-6 w-6 mb-1 ${
                              item.status === 'fail' ? 'text-red-600' : 'text-slate-500'
                            }`} />
                            <span className={`text-sm font-medium text-center leading-tight ${
                              item.status === 'fail' ? 'text-red-700' : 'text-gray-600'
                            }`}>
                              Major Defect
                            </span>
                          </div>
                        </button>

                        <button
                          onClick={() => updateInspectionItem(item.id, 'status', 'na')}
                          data-testid={`item-${item.id}-na`}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            item.status === 'na'
                              ? 'bg-gray-100 border-gray-500 shadow-md'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <NoSymbolIcon className={`h-6 w-6 mb-1 ${
                              item.status === 'na' ? 'text-gray-700' : 'text-slate-500'
                            }`} />
                            <span className={`text-sm font-medium ${
                              item.status === 'na' ? 'text-gray-800' : 'text-gray-600'
                            }`}>
                              N/A
                            </span>
                          </div>
                        </button>
                      </div>

                      {/* Notes and Photos for Issues */}
                      {(item.status === 'fail' || item.status === 'needs_attention') && (
                        <div className="mt-3 pt-3 border-t border-gray-300 space-y-3">
                          <textarea
                            value={item.notes || ''}
                            onChange={(e) => updateInspectionItem(item.id, 'notes', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg text-gray-900 text-sm"
                            rows="3"
                            placeholder="Describe the issue and corrective actions taken..."
                          />
                          
                          {/* Photo Upload Section */}
                          <div className={`rounded-lg border-2 border-dashed p-3 ${
                            missingPhoto ? 'bg-red-50 border-red-400' : 'bg-white border-gray-300'
                          }`}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              📸 Photo Documentation {requiresPhoto && <span className="text-red-600 font-bold">(REQUIRED FOR DEFECTS)</span>}
                            </label>
                            
                            {/* Photo Thumbnails */}
                            {item.photos && item.photos.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {item.photos.map((photo, photoIndex) => (
                                  <div key={photoIndex} className="relative">
                                    <img 
                                      src={photo} 
                                      alt={`Defect ${photoIndex + 1}`}
                                      className="w-20 h-20 object-cover rounded border-2 border-gray-300"
                                      onClick={() => window.open(photo, '_blank')}
                                    />
                                    <button
                                      onClick={() => removePhoto(item.id, photoIndex)}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-lg"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Photo Upload Buttons */}
                            <div className="flex gap-2">
                              <label className="flex-1 cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={(e) => handlePhotoCapture(e, item.id)}
                                  className="hidden"
                                />
                                <div className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                  <CameraIcon className="h-5 w-5 mr-2" />
                                  Take Photo
                                </div>
                              </label>
                              
                              <label className="flex-1 cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => handlePhotoUpload(e, item.id)}
                                  className="hidden"
                                />
                                <div className="flex items-center justify-center px-4 py-3 bg-slate-200 text-slate-900 rounded-lg hover:bg-slate-100 text-sm font-medium">
                                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                                  Upload
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Overall Notes */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Additional Notes</h2>
          <textarea
            value={formData.notes}
            onChange={(e) => updateFormData('notes', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg"
            rows="4"
            placeholder="Add any additional notes or corrective actions..."
          />
        </div>

        {/* Signature Pad - For equipment inspections */}
        {(() => {
          const shouldShowSignature = (
            inspectionType === 'truck_inspection' || 
            inspectionType === 'forklift_inspection' ||
            inspectionType === 'aerial_lift_inspection' ||
            inspectionType === 'overhead_crane_inspection' ||
            inspectionType === 'harness_lanyard_inspection' ||
            inspectionType === 'trailer_inspection' ||
            inspectionType === 'confined_space_permit' ||
            inspectionType === 'lockout_tagout'
          );
          
          console.log('Signature check:', { inspectionType, shouldShowSignature });
          
          return shouldShowSignature;
        })() && (
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  {inspectionType === 'confined_space_permit' ? 'Entry Supervisor Signature — Opens Permit' : 'Inspector Signature'}
                </h2>
                <span className="ml-2 text-red-600 font-bold text-sm">* REQUIRED</span>
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="text-red-600 hover:text-red-700 flex items-center text-sm"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            </div>
            
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                onEnd={saveSignature}
                canvasProps={{
                  className: 'w-full h-40',
                  style: { touchAction: 'none' }
                }}
                backgroundColor="white"
              />
            </div>
            
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <PencilIcon className="h-4 w-4 mr-1" />
              <span>Sign above with your finger or stylus</span>
            </div>
            
            {signature && (
              <div className="mt-3 flex items-center text-green-600 text-sm">
                <CheckCircleIcon className="h-5 w-5 mr-1" />
                <span>Signature captured</span>
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className="sticky bottom-4">
          {locked ? (
            <div className="w-full bg-amber-100 border border-amber-300 text-amber-800 font-semibold py-4 px-6 rounded-lg shadow-lg flex items-center justify-center" data-testid="inspection-locked-save">
              <NoSymbolIcon className="h-6 w-6 mr-2" />
              Locked — manager approval required to edit
            </div>
          ) : (
            <button
              onClick={saveInspection}
              disabled={saving}
              data-testid="save-inspection-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg flex items-center justify-center disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <ClipboardDocumentCheckIcon className="h-6 w-6 mr-2" />
                  Save Inspection
                </>
              )}
            </button>
          )}
        </div>
      </div>
      </fieldset>

      {/* Bottom padding */}
      <div className="h-20"></div>
    </div>
  );
};

export default MobileInspectionForm;